'use client';

import { useParams } from 'next/navigation';
import { useMemo } from 'react';
import { CITIES, useAiFoodOrderStore } from '@/store';

/**
 * Hook để xử lý điều hướng với city_slug dựa trên địa chỉ của user
 * Thay thế các ROUTES cũ để hỗ trợ cấu trúc URL mới: /city_slug/...
 */
export function useCityNavigation() {
  const params = useParams();
  const address = useAiFoodOrderStore.use.address();
  const cityId = useAiFoodOrderStore.use.city_id();

  // Lấy city_slug từ URL hiện tại hoặc từ store
  const citySlug = useMemo(() => {
    // Ưu tiên city_slug từ URL hiện tại
    if (params.city && typeof params.city === 'string') {
      return params.city;
    }

    // Nếu không có trong URL, tìm từ cityId trong store
    if (cityId) {
      const city = CITIES.find((c) => c.value === cityId);
      if (city) {
        return city.slug;
      }
    }

    // Nếu không có cityId, cố gắng tìm từ address
    if (address?.address ?? address?.second_label) {
      const addressText = address.second_label ?? address.address ?? '';
      const matchedCity = CITIES.find((city) =>
        addressText.includes(city.label)
      );
      if (matchedCity) {
        return matchedCity.slug;
      }
    }

    // Fallback về Đà Nẵng nếu không tìm thấy
    return 'da-nang';
  }, [params.city, cityId, address]);

  // Các function để tạo URLs với city_slug
  const routes = useMemo(
    () => ({
      // Trang chủ
      home: `/${citySlug}`,

      // Danh sách cửa hàng (search)
      search: `/${citySlug}/danh-sach-cua-hang`,

      // Chi tiết cửa hàng - Route mới với city_slug
      storeDetail: (storeSlug: string) => `/cua-hang/${storeSlug}`,

      // Order routes cho luồng chính (desktop/mobile web)
      orderStore: (storeId: string) => `/order/${storeId}`,
      orderCart: (storeId: string) => `/order/${storeId}/cart`,

      // Order routes cho webview (luồng /m)
      orderStore_wv: (storeId: string) => `/m/order-wv/${storeId}`,
      orderCart_wv: (storeId: string) => `/m/order-wv/${storeId}/cart`,

      // User routes
      user: '/user',
      orderList: '/order-list',

      // Static routes
      sentryExamplePage: '/sentry-example-page',
      order: '/order',
      store: '/store',
    }),
    [citySlug]
  );

  return {
    citySlug,
    routes,
    /**
     * Get city slug từ address text
     */
    getCitySlugFromAddress: (addressText: string) => {
      const matchedCity = CITIES.find((city) =>
        addressText.includes(city.label)
      );
      return matchedCity?.slug ?? 'da-nang';
    },

    /**
     * Check if current city slug matches address
     */
    isValidCityForAddress: (addressText: string) => {
      const expectedSlug = CITIES.find((city) =>
        addressText.includes(city.label)
      )?.slug;
      return expectedSlug === citySlug;
    },
  };
}

/**
 * Hook đơn giản chỉ để lấy city routes
 * Dùng khi chỉ cần access routes mà không cần logic phức tạp
 */
export function useCityRoutes() {
  const { routes } = useCityNavigation();
  return routes;
}
